import EventLayout from '@/components/layouts/EventLayout'
import { PageHeader } from '@/components/patterns'
import { Button, Input, Select, message } from '@/components/ui'
import { api } from '@/lib/apiClient'
import { Head } from '@inertiajs/react'
import { useState } from 'react'
import { HiOutlinePlus, HiOutlineTrash } from 'react-icons/hi'

type ButtonAction = 'event_home' | 'login' | 'registration' | 'custom_link'

interface ButtonItem {
  text: string
  action: ButtonAction
  url?: string
}

const ACTION_OPTIONS = [
  { label: 'Go to event home', value: 'event_home' },
  { label: 'Go to login page', value: 'login' },
  { label: 'Go to registration page', value: 'registration' },
  { label: 'Custom link', value: 'custom_link' },
]

const DEFAULT_TEXT_FOR_ACTION: Record<ButtonAction, string> = {
  event_home: 'Back to Event Home',
  login: 'Login',
  registration: 'Register',
  custom_link: '',
}

interface ThankYouPageProps {
  eventName: string
  eventSlug: string
  thankYouPage: {
    title: string
    description: string
    buttons: ButtonItem[]
  }
}

export default function ThankYouPageSettings({
  eventName,
  eventSlug,
  thankYouPage,
}: ThankYouPageProps) {
  const [title, setTitle] = useState(thankYouPage.title)
  const [description, setDescription] = useState(thankYouPage.description)
  const [buttons, setButtons] = useState<ButtonItem[]>(
    thankYouPage.buttons.length > 0
      ? thankYouPage.buttons
      : [{ text: 'Back to Event Home', action: 'event_home' }],
  )
  const [isLoading, setIsLoading] = useState(false)

  const addButton = () => {
    setButtons([...buttons, { text: '', action: 'event_home' }])
  }

  const removeButton = (index: number) => {
    setButtons(buttons.filter((_, i) => i !== index))
  }

  const updateButton = (index: number, updates: Partial<ButtonItem>) => {
    const updated = [...buttons]
    updated[index] = { ...updated[index], ...updates }
    setButtons(updated)
  }

  const handleActionChange = (index: number, action: ButtonAction) => {
    const btn = buttons[index]
    const updates: Partial<ButtonItem> = { action }
    // Auto-fill text if currently empty or matches a default
    const isDefaultText = Object.values(DEFAULT_TEXT_FOR_ACTION).includes(btn.text)
    if (!btn.text || isDefaultText) {
      updates.text = DEFAULT_TEXT_FOR_ACTION[action]
    }
    if (action !== 'custom_link') {
      updates.url = undefined
    }
    updateButton(index, updates)
  }

  const handleSave = async () => {
    setIsLoading(true)
    try {
      const { data } = await api.patch<any>(`/events/${eventSlug}/thank_you_page`, {
        thank_you_page: {
          title,
          description,
          buttons: buttons.filter((b) => b.text.trim()),
        },
      })
      const next = data?.thankYouPage
      if (next) {
        setTitle(next.title || '')
        setDescription(next.description || '')
        setButtons(
          next.buttons?.length > 0
            ? next.buttons
            : [{ text: 'Back to Event Home', action: 'event_home' }],
        )
      }
      message.success('Thank you page updated successfully.')
    } catch (error) {
      console.error('Error updating thank you page:', error)
      message.error('Failed to update thank you page.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <EventLayout>
      <Head title={`${eventName} - Thank You Page`} />
      <PageHeader
        title="Thank You Page"
        subtitle="Customize the page shown to attendees after they register."
      />
      <div className="p-4">
        <div className="bg-white flex flex-col max-w-[600px] border border-gray-100">
          <div className="px-8 py-5 border-b border-gray-200">
            <p className="m-0 text-xl leading-5">Content</p>
          </div>
          <div className="p-8 flex flex-col gap-6">
            <div>
              <label className="block text-sm font-medium mb-1">Title</label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Thank you for registering!"
              />
              <p className="text-xs text-black/40 mt-1">
                Leave blank to use default: "Thank you for registering!"
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Your registration has been received successfully."
              />
              <p className="text-xs text-black/40 mt-1">
                Leave blank to use default: "Your registration has been received successfully."
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Buttons</label>
              <div className="flex flex-col gap-4">
                {buttons.map((btn, index) => (
                  <div
                    key={index}
                    className="flex gap-2 items-start border border-gray-200 rounded-md p-3"
                  >
                    <div className="flex-1 flex flex-col gap-2">
                      <Input
                        value={btn.text}
                        onChange={(e) => updateButton(index, { text: e.target.value })}
                        placeholder="Button text"
                      />
                      <Select
                        value={btn.action}
                        onChange={(value) => handleActionChange(index, value as ButtonAction)}
                        options={ACTION_OPTIONS}
                        className="w-full"
                      />
                      {btn.action === 'custom_link' && (
                        <Input
                          value={btn.url || ''}
                          onChange={(e) => updateButton(index, { url: e.target.value })}
                          placeholder="https://example.com"
                        />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeButton(index)}
                      className="mt-1 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                    >
                      <HiOutlineTrash className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addButton}
                className="mt-3 flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
              >
                <HiOutlinePlus className="w-4 h-4" />
                Add button
              </button>
            </div>

            <div className="pt-2">
              <Button type="primary" onClick={handleSave} loading={isLoading}>
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>
    </EventLayout>
  )
}
